/**
 * Pick which Instagram professional account the Instagram module reads from.
 * Lists every IG account the connected Meta token can see (one per linked
 * Facebook Page); the current one is marked. Selecting links it to the default
 * Meta ad account — the same field Ad Account settings edits.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import toast from 'react-hot-toast';
import { AlertCircle, Camera, Check, RefreshCw } from 'lucide-react';
import clsx from 'clsx';
import { metaResourcesService, MetaInstagramAccount } from '@/services/meta-resources.service';
import { facebookAdAccountService } from '@/services/facebook-ad-account.service';

export function IgAccountSwitchModal({ open, currentId, onClose, onSwitched }: {
  open: boolean;
  currentId: string | null;
  onClose: () => void;
  onSwitched: (account: MetaInstagramAccount) => void;
}) {
  const [accounts, setAccounts] = useState<MetaInstagramAccount[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    setAccounts(null); setError(null);
    try { setAccounts(await metaResourcesService.instagramAccounts()); }
    catch (e: any) { setAccounts([]); setError(e?.response?.data?.message ?? 'Could not load Instagram accounts'); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const pick = async (a: MetaInstagramAccount) => {
    if (a.id === currentId) { onClose(); return; }
    setBusy(a.id);
    try {
      await facebookAdAccountService.connectInstagram(a.id);
      toast.success(`Switched to @${a.username ?? a.id}`);
      onSwitched(a);
    } catch (e: any) {
      if (!e?.response) toast.error(e?.message ?? 'Could not switch account');
    } finally { setBusy(null); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Switch Instagram account">
      <p className="text-xs text-text-muted">
        Instagram professional accounts linked to the Facebook Pages your Meta connection can see.
        Not listed? Link the account to a Page in Meta Business Suite, then reload.
      </p>
      {error && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-xs text-text-secondary">
          <AlertCircle size={14} className="mt-0.5 shrink-0 text-warning" />
          <span>{error}. The Meta token needs <code>instagram_basic</code> + <code>pages_show_list</code> — reconnect under{' '}
            <Link to="/facebook/accounts" className="text-primary underline">Facebook Ads → Accounts</Link>.</span>
        </div>
      )}
      <div className="mt-3 max-h-80 space-y-1.5 overflow-y-auto">
        {accounts === null ? (
          [0, 1].map((i) => <div key={i} className="skeleton h-14 rounded-xl" />)
        ) : accounts.length === 0 && !error ? (
          <div className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-xs text-text-muted">
            No Instagram professional accounts found on this Meta connection.
          </div>
        ) : accounts.map((a) => {
          const current = a.id === currentId;
          return (
            <button key={a.id} type="button" disabled={!!busy} onClick={() => pick(a)}
              className={clsx(
                'flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors disabled:opacity-60',
                current ? 'border-primary bg-primary-soft/50' : 'border-border bg-surface hover:border-primary/40 hover:bg-surface-alt',
              )}>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-surface-alt">
                {a.profilePictureUrl ? <img src={a.profilePictureUrl} alt="" className="h-full w-full object-cover" /> : <Camera size={16} className="text-text-muted" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-text-primary">@{a.username ?? a.id}</span>
                <span className="block font-mono text-2xs text-text-muted">{a.id}</span>
              </span>
              {current ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-primary-soft px-2 py-0.5 text-2xs font-semibold text-primary"><Check size={11} />Connected</span>
              ) : busy === a.id ? (
                <RefreshCw size={14} className="animate-spin text-text-muted" />
              ) : (
                <span className="text-2xs font-semibold text-primary">Use this</span>
              )}
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex items-center justify-between gap-2">
        <button type="button" onClick={load} className="inline-flex items-center gap-1 text-2xs text-text-muted hover:text-text-primary">
          <RefreshCw size={11} />Reload list
        </button>
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </div>
    </Modal>
  );
}
